/**
 * Local-asset helpers for the Itemizer (GH #24).
 *
 * Supports the drag-a-file → strip-background → save flow on the Active
 * Item panel:
 *   - blobToRaster: transcode/resize a dropped raster (WebP for storage,
 *     PNG for the rembg round-trip). Alpha preserved, so stripped tokens
 *     stay transparent.
 *   - blobToBase64 / base64ToBlob: marshal bytes for the `stripBackground`
 *     callable (which proxies to the rembg Cloud Run service so results
 *     match the pipeline's model — no second in-browser remover).
 *   - uploadToLibrary: content-addressed upload to the same admin-write
 *     `items/library/icon-<sha>.webp` path the pipeline + stamp worker use.
 *
 * All browser-only (canvas / crypto.subtle); call from event handlers,
 * never during SSR.
 */
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
	const digest = await crypto.subtle.digest('SHA-256', buf);
	return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 (hex) of a blob's bytes — same digest used for library paths. */
export async function sha256OfBlob(blob: Blob): Promise<string> {
	return sha256Hex(await blob.arrayBuffer());
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(blob);
		const img = new Image();
		img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
		img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not decode image')); };
		img.src = url;
	});
}

/**
 * Transcode a raster blob, downscaling so the longest edge is at most
 * `maxDim`. Alpha preserved. `mime` selects the output codec
 * (`image/webp` for storage, `image/png` for the lossless rembg
 * round-trip); `quality` applies to lossy codecs only.
 */
export async function blobToRaster(
	blob: Blob, maxDim = 1024, mime: 'image/webp' | 'image/png' = 'image/webp', quality = 0.92,
): Promise<Blob> {
	const img = await loadImage(blob);
	let w = img.naturalWidth || img.width;
	let h = img.naturalHeight || img.height;
	if (maxDim && Math.max(w, h) > maxDim) {
		const s = maxDim / Math.max(w, h);
		w = Math.round(w * s);
		h = Math.round(h * s);
	}
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('No 2D canvas context');
	ctx.clearRect(0, 0, w, h);
	ctx.drawImage(img, 0, 0, w, h);
	return await new Promise<Blob>((resolve, reject) => {
		canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), mime, quality);
	});
}

/** WebP convenience wrapper (storage uploads). */
export const blobToWebp = (blob: Blob, maxDim = 1024, quality = 0.92): Promise<Blob> =>
	blobToRaster(blob, maxDim, 'image/webp', quality);

/** Blob → base64 (no data: prefix), for callable payloads. */
export async function blobToBase64(blob: Blob): Promise<string> {
	const buf = await blob.arrayBuffer();
	let binary = '';
	const bytes = new Uint8Array(buf);
	const chunk = 0x8000;
	for (let i = 0; i < bytes.length; i += chunk) {
		binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
	}
	return btoa(binary);
}

/** base64 → Blob of the given MIME (default PNG). */
export function base64ToBlob(b64: string, mime = 'image/png'): Blob {
	const binary = atob(b64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return new Blob([bytes], { type: mime });
}

/** Upload a WebP blob to the content-addressed library; returns its URL. */
export async function uploadToLibrary(webp: Blob): Promise<string> {
	const sha = await sha256Hex(await webp.arrayBuffer());
	const path = `items/library/icon-${sha}.webp`;
	const r = storageRef(getStorage(), path);
	await uploadBytes(r, webp, {
		contentType: 'image/webp',
		cacheControl: 'public, max-age=31536000, immutable',
	});
	return await getDownloadURL(r);
}
